import {
	type GraphQLSingularResponse,
	type GraphQLResponse,
	type CacheConfig,
	type RequestParameters,
	type Variables,
	Observable,
} from 'relay-runtime';
import delay from 'lodash/delay';

import { getRelayExperimentalApis } from '@atlassian/relay-experimental-apis';
import { type AGGError, captureErrors } from '@atlassian/relay-errors';

import {
	isFastPreloadQuery,
	preloadQueryBatchConfigGeneratedAt,
	preloadQueryBatchConfigSourceUrl,
} from '@confluence/graphql/entry-points/preloadQueryBatchConfig.ts';
import { getQueryName } from '@confluence/query-preloader-tools/entry-points/getQueryName.ts';
import {
	createWrappedFetch,
	type WrappedFetchRequestInit,
} from '@confluence/graphql/entry-points/wrappedFetch.ts';
import { StatsigConfigurations } from '@confluence/statsig-configurations/entry-points/StatsigConfigurations.ts';
import { NoNetworkError } from '@confluence/network';
import { fg } from '@confluence/feature-gating';

import {
	applyRelayQueryOverrides,
	shouldEnableQueryOverrides,
} from './applyRelayQueryOverrides.ts';
import {
	getAggEndpoint,
	getBatchEndpoint,
	getPQBatchEndpoint,
	getPQBatchRequestBody,
	getRequestBody,
	isNoQueryTextOperation,
	shouldUsePQEndpoint,
} from './aggEndpoint.ts';
import { calculateExponentialBackoff } from './calculateExponentialBackoff.ts';
import { recordRelaySSRHydrationResult } from './relaySSRHydrationContext.ts';
import {
	emitRelayPayload,
	INCREMENTAL_ACCEPT_HEADER,
	isIncrementalRequest,
	readGraphQLResponsePayloads,
	type GraphQLResponsePayload,
} from './incrementalResponse.ts';

const wrappedFetch = createWrappedFetch({ isForRelay: true });

const MAX_CONCURRENCY_FOR_THROTTLED = 8;
const NETWORK_ERROR_MAX_RETRIES = 2;
const NETWORK_ERROR_INITIAL_RETRY_DELAY_MS = 500;
const NETWORK_ERROR_MAX_RETRY_DELAY_MS = 5000;

/**
 * Per-request HTTP headers a caller supplied via `cacheConfig.metadata.headers`
 * (e.g. the `X-Query-Context` header AGG requires for `graphStore_*` fields).
 * Relay stashes them on cache metadata; the transport must forward them onto the
 * outgoing fetch or they are silently dropped and the gateway rejects the request.
 * Only honoured on the single-request path — a request carrying custom headers
 * bypasses batching (see `shouldBypassBatch`) so a header meant for one operation
 * is never applied to co-batched operations sharing the HTTP request.
 */
const getMetadataHeaders = (cacheConfig: CacheConfig): Record<string, string> => {
	const headers = cacheConfig.metadata?.headers;
	if (headers == null || typeof headers !== 'object') {
		return {};
	}
	// `metadata` is loosely typed (`{ [key: string]: any }`), so filter to string
	// values only — non-string entries would otherwise be coerced into malformed
	// outgoing HTTP headers.
	return Object.fromEntries(
		Object.entries(headers as Record<string, unknown>).filter(
			(entry): entry is [string, string] => typeof entry[1] === 'string',
		),
	);
};

const BATCH_SAFE_METADATA_HEADER_NAMES = new Set(['atl-attribution']);

const isBatchSafeMetadataHeader = (headerName: string): boolean =>
	BATCH_SAFE_METADATA_HEADER_NAMES.has(headerName.toLowerCase());

const getBatchSafeMetadataHeaders = (cacheConfig: CacheConfig): Record<string, string> => {
	const batchSafeMetadataHeaders: Record<string, string> = {};

	Object.entries(getMetadataHeaders(cacheConfig)).forEach(([headerName, headerValue]) => {
		const normalizedHeaderName = headerName.toLowerCase();
		if (BATCH_SAFE_METADATA_HEADER_NAMES.has(normalizedHeaderName)) {
			batchSafeMetadataHeaders[normalizedHeaderName] = headerValue;
		}
	});

	return batchSafeMetadataHeaders;
};

const getCommonBatchSafeMetadataHeaders = (requests: BatchedRequest[]): Record<string, string> => {
	const [firstRequest] = requests;
	if (!firstRequest) {
		return {};
	}

	const firstRequestHeaders = getBatchSafeMetadataHeaders(firstRequest.cacheConfig);

	return Object.fromEntries(
		Object.entries(firstRequestHeaders).filter(([headerName, headerValue]) =>
			requests.every(
				(request) => getBatchSafeMetadataHeaders(request.cacheConfig)[headerName] === headerValue,
			),
		),
	);
};

const sendTrackEvent = (
	error: NoNetworkError,
	{
		queryName,
		priorRetries = 0,
	}: {
		queryName: string;
		priorRetries?: number;
	},
) => {
	void import(
		/* webpackChunkName: "loadable-confluenceanalytics-web-client" */ '@confluence/analytics-web-client'
	).then(({ getAnalyticsWebClient }) => {
		void getAnalyticsWebClient().then((analyticsWebClient: any) => {
			analyticsWebClient.sendOperationalEvent({
				source: 'ui',
				actionSubject: 'graphql',
				action: 'errored',
				attributes: {
					eventName: 'network.error.retry',
					queryName,
					priorRetries,
					message: error.message,
				},
			});
		});
	});
};

const requestPool: Promise<Response>[] = [];
const throttledFetch = (uri: string, options: WrappedFetchRequestInit): Promise<Response> => {
	if (requestPool.length >= MAX_CONCURRENCY_FOR_THROTTLED) {
		const queuedFetchFn = async () => {
			await requestPool.shift();
			return wrappedFetch(uri, options);
		};

		const queuedFetch: Promise<Response> = queuedFetchFn();
		requestPool.push(queuedFetch);
		return queuedFetch;
	}

	const nextFetch = wrappedFetch(uri, options);
	requestPool.push(nextFetch);
	return nextFetch;
};

const fetchFunctions = {
	throttledFetch,
	wrappedFetch,
};

export const __TEST_ONLY__: {
	requestPool: Promise<Response>[];
	fetchFunctions: {
		throttledFetch: (uri: string, options: WrappedFetchRequestInit) => Promise<Response>;
		wrappedFetch: (uri: string, options: WrappedFetchRequestInit) => Promise<Response>;
	};
} = {
	requestPool,
	fetchFunctions,
};

const shouldDisableBatchingForEnvironment = (): boolean => {
	if (typeof window === 'undefined') {
		return false;
	}

	return (
		Boolean((window as any).Cypress) ||
		Boolean((window as any).__CRITERION__) ||
		Boolean((window as any).isPlaywright) ||
		Boolean((window as any).isContract) ||
		!StatsigConfigurations.isKillSwitchOn('confluence_spa_query_batching')
	);
};

const retryFetch = async (
	fetchFn: () => Promise<any>,
	{
		onErrorFn,
		shouldRetryFn,
		onRetryFn,
		maxRetries,
		initialDelayMs,
		maxDelayMs,
	}: {
		onErrorFn?: (error: Error, attempt: number) => void;
		shouldRetryFn?: (error: Error, attempt: number) => boolean;
		onRetryFn?: (error: Error, attempt: number) => void;
		maxRetries: number;
		initialDelayMs: number;
		maxDelayMs: number;
	},
): Promise<any> => {
	for (let attempt = 1; attempt <= maxRetries; attempt++) {
		try {
			return await fetchFn();
		} catch (error) {
			if (!shouldRetryFn?.(error, attempt) || attempt === maxRetries) {
				if (onErrorFn) {
					return onErrorFn(error as Error, attempt);
				} else {
					throw error;
				}
			}

			if (onRetryFn) {
				onRetryFn(error, attempt);
			}

			const exponentialDelay = calculateExponentialBackoff(attempt, initialDelayMs, maxDelayMs);

			// for testing, we want to avoid actual delays to prevent timeouts
			if (process.env.NODE_ENV !== 'testing') {
				await new Promise((resolve) => delay(resolve, exponentialDelay));
			}
		}
	}
};

interface BatchingConfig {
	/** Maximum number of requests to batch together */
	maxBatchSize: number;
	/** Maximum time to wait before sending a batch (in milliseconds) */
	batchInterval: number;
	/** Whether batching is enabled */
	enabled?: boolean;
}

const DEFAULT_SSR_BATCHING_CONFIG: BatchingConfig = {
	maxBatchSize: 4,
	batchInterval: 0,
	enabled: true,
};

export const DEFAULT_BATCHING_CONFIG: BatchingConfig = {
	maxBatchSize: 10,
	batchInterval: 10,
	enabled: true,
};

export const getDefaultBatchingConfig = (): BatchingConfig => {
	// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
	const isSSR = Boolean(process.env.REACT_SSR);
	if (isSSR) {
		return {
			...DEFAULT_SSR_BATCHING_CONFIG,
			maxBatchSize: StatsigConfigurations.getNumber(
				'confluence_relay_batch_size',
				DEFAULT_SSR_BATCHING_CONFIG.maxBatchSize,
			),
			batchInterval: StatsigConfigurations.getNumber(
				'confluence_relay_batch_interval',
				DEFAULT_SSR_BATCHING_CONFIG.batchInterval,
			),
		};
	}

	return {
		...DEFAULT_BATCHING_CONFIG,
		maxBatchSize: StatsigConfigurations.getNumber(
			'confluence_relay_batch_size_client',
			DEFAULT_BATCHING_CONFIG.maxBatchSize,
		),
		batchInterval: StatsigConfigurations.getNumber(
			'confluence_relay_batch_interval_client',
			DEFAULT_BATCHING_CONFIG.batchInterval,
		),
	};
};

interface BatchedRequest {
	request: RequestParameters;
	variables: Variables;
	cacheConfig: CacheConfig;
	resolve: (value: GraphQLSingularResponse) => void;
	reject: (error: Error) => void;
	id: string;
}

const getErrorMessage = (error: unknown): string => {
	if (error instanceof Error) {
		return error.message;
	}

	return String(error);
};

const getResponseBodyDebugInfo = async (response: Response) => {
	try {
		return {
			text: await response.clone().text(),
		};
	} catch (error) {
		return {
			textReadError: getErrorMessage(error),
		};
	}
};

const stringifyDebugInfo = (debugInfo: unknown): string => {
	try {
		return JSON.stringify(debugInfo);
	} catch (error) {
		return JSON.stringify({
			jsonStringifyError: getErrorMessage(error),
		});
	}
};

const logPreloadBatchDebug = (message: string, debugInfo: unknown): void => {
	// oxlint-disable-next-line confluence-feature-gating/no-new-feature-gates
	if (!fg('confluence_relay_preload_batch_debug_logs')) {
		return;
	}

	// eslint-disable-next-line no-console
	console.log(`[Relay] ${message} ${stringifyDebugInfo(debugInfo)}`);
};

const logBatchResponseDebugInfo = async ({
	response,
	batchEndpoint,
	operationNames,
	usePQ,
}: {
	response: Response;
	batchEndpoint: string;
	operationNames: string[];
	usePQ: boolean;
}) => {
	const responseBody = await getResponseBodyDebugInfo(response);

	// eslint-disable-next-line no-console
	console.log(
		`[DEBUG] Relay batch request failed ${stringifyDebugInfo({
			batchEndpoint,
			usePQ,
			operationNames,
			response: {
				status: response.status,
				statusText: response.statusText,
				url: response.url,
				...responseBody,
			},
		})}`,
	);
};

type SingleRequestOptions = {
	shouldThrottle?: boolean;
	shouldBypassBatch?: boolean;
	signal?: AbortSignal;
	incremental?: boolean;
};

/**
 * BatchFetcher is a class that fetches multiple requests in a batch. Incoming requests are queued and processed in batches with a
 * configured max batch size and batch interval. If batching is disabled, requests are processed individually using processSingleRequest.
 * The batch endpoint URL (/slow/batch or /pq/batch) is chosen at dispatch time based on the PQ experiment.
 *
 * Throttling behavior (mirrors Apollo's implementation):
 * - When metadata.throttle is set to true, requests bypass batching and use throttledFetch
 * - throttledFetch limits concurrent network requests to MAX_CONCURRENCY_FOR_THROTTLED (8)
 * - This is useful when a large volume of queries are triggered together, such as upon page load
 *
 * Usage: fetchQuery(environment, query, variables, { metadata: { throttle: true } })
 */
export class BatchFetcher {
	private batchingConfig: BatchingConfig;
	private requestQueue: BatchedRequest[];
	private batchTimeout: ReturnType<typeof setTimeout> | null = null;

	constructor(config: BatchingConfig = getDefaultBatchingConfig()) {
		this.batchingConfig = config;
		this.requestQueue = [];
	}

	private generateRequestId(): string {
		return `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
	}

	private shouldRetryFetch(error: Error, requests: RequestParameters[]): boolean {
		// Follow retry logic from confluence/next/packages/graphql/src/links/NetworkErrorRetryLink.ts

		// That link is client-only — `createLink` returns early for REACT_SSR, so Apollo's SSR chain has
		// no retry at all and makes exactly one attempt. Matching that is a parity choice, not a timeout
		// guard: the prepare-phase budget (`confluence_ssr_-_prepare_phase_timeout`) is not configured in
		// Statsig, so it falls back to `UNREACHABLE_TIMEOUT` (60s) and a retry's backoff is not competing
		// for anything scarce. If a tighter per-request deadline is ever introduced upstream (Bifrost or
		// Tesseract), that becomes a second, independent reason to keep this.
		// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
		if (process.env.REACT_SSR) {
			return false;
		}

		// `every`, not `some`: Apollo excludes mutations per operation, so a batch carrying a mutation
		// alongside queries must not be retried either — retrying it risks a double write.
		return (
			requests.every((req) => req.operationKind !== 'mutation') && error instanceof NoNetworkError
		);
	}

	// Send track event when a retry is attempted
	private onRetryFn(error: Error, requests: RequestParameters[], attempt: number): void {
		if (error instanceof NoNetworkError) {
			requests.forEach((req) => {
				sendTrackEvent(error, {
					queryName: req.name,
					// `attempt` is the 1-based attempt that just failed, so retries already made is one
					// fewer. Apollo reports the same quantity by reading `context.networkRetries` before
					// incrementing it, which is 0 on the first retry — keep them comparable.
					priorRetries: attempt - 1,
				});
			});
		}
	}

	private processGraphQLSingularResponse(
		parsedResponse: GraphQLSingularResponse,
		request: RequestParameters,
	): GraphQLSingularResponse {
		if ('errors' in parsedResponse && parsedResponse.errors && parsedResponse.errors.length > 0) {
			const errors = parsedResponse.errors as AGGError[];
			const requestId = parsedResponse.extensions?.gateway?.request_id;

			captureErrors(errors, {
				operationName: request.name,
				requestId,
			});
		}

		if (shouldEnableQueryOverrides()) {
			return applyRelayQueryOverrides(parsedResponse, request);
		}

		return parsedResponse;
	}

	private processGraphQLResponsePayload(
		payload: GraphQLResponsePayload,
		request: RequestParameters,
	): GraphQLResponsePayload {
		if (Array.isArray(payload)) {
			return payload.map((item) => this.processGraphQLSingularResponse(item, request));
		}

		if ('incremental' in payload && Array.isArray(payload.incremental)) {
			return {
				...payload,
				incremental: payload.incremental.map((item) =>
					this.processGraphQLSingularResponse(item, request),
				),
			};
		}

		return this.processGraphQLSingularResponse(
			payload as GraphQLSingularResponse,
			request,
		) as GraphQLResponsePayload;
	}

	private async executeSingleRequest(
		request: RequestParameters,
		variables: Variables,
		cacheConfig: CacheConfig,
		{
			shouldThrottle,
			shouldBypassBatch,
			signal,
			incremental = isIncrementalRequest(cacheConfig),
		}: SingleRequestOptions = {},
	): Promise<Response> {
		const endpoint = getAggEndpoint(request, cacheConfig);
		const body = getRequestBody(request, variables);
		const fetchFn = shouldThrottle ? fetchFunctions.throttledFetch : fetchFunctions.wrappedFetch;
		const headers: Record<string, string> = {
			// Caller-supplied headers first so the transport's own headers below always win
			// and can never be clobbered (e.g. a caller cannot override `content-type`).
			...getMetadataHeaders(cacheConfig),
			'content-type': 'application/json',
			'X-ExperimentalApi': getRelayExperimentalApis().join(','),
		};
		if (incremental) {
			headers.Accept = INCREMENTAL_ACCEPT_HEADER;
		}
		const requestOptions: WrappedFetchRequestInit = {
			method: 'POST',
			credentials: 'same-origin',
			headers,
			body,
			confluenceRelayFetchOptions: {
				shouldConsiderGetMethod: shouldBypassBatch,
				operationKind: request.operationKind,
			},
		};
		if (signal) {
			requestOptions.signal = signal;
		}

		return fetchFn(endpoint, requestOptions);
	}

	private async processSingleRequest(
		request: RequestParameters,
		variables: Variables,
		cacheConfig: CacheConfig,
		shouldThrottle?: boolean,
		shouldBypassBatch?: boolean,
	): Promise<any> {
		return retryFetch(
			async () => {
				const response = await this.executeSingleRequest(request, variables, cacheConfig, {
					shouldThrottle,
					shouldBypassBatch,
				});

				let parsedResponse: GraphQLSingularResponse = await response.json();
				recordRelaySSRHydrationResult(parsedResponse);
				parsedResponse = this.processGraphQLSingularResponse(parsedResponse, request);
				return parsedResponse;
			},
			{
				shouldRetryFn: (error) => this.shouldRetryFetch(error, [request]),
				onRetryFn: (error, attempt) => this.onRetryFn(error, [request], attempt),
				maxRetries: NETWORK_ERROR_MAX_RETRIES,
				initialDelayMs: NETWORK_ERROR_INITIAL_RETRY_DELAY_MS,
				maxDelayMs: NETWORK_ERROR_MAX_RETRY_DELAY_MS,
			},
		);
	}

	public fetchIncremental(
		request: RequestParameters,
		variables: Variables,
		cacheConfig: CacheConfig,
	): Observable<GraphQLResponse> {
		return Observable.create<GraphQLResponse>((observer) => {
			const abortController = new AbortController();
			let unsubscribed = false;
			const shouldThrottle =
				// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
				!process.env.REACT_SSR &&
				!shouldDisableBatchingForEnvironment() &&
				cacheConfig.metadata?.throttle === true;

			void retryFetch(
				async () => {
					const response = await this.executeSingleRequest(request, variables, cacheConfig, {
						shouldThrottle,
						shouldBypassBatch: true,
						signal: abortController.signal,
						incremental: true,
					});

					for await (const payload of readGraphQLResponsePayloads(response)) {
						if (unsubscribed) {
							return;
						}

						const processedPayload = this.processGraphQLResponsePayload(payload, request);
						const isFinalPayload = emitRelayPayload(processedPayload, observer);
						if (isFinalPayload) {
							observer.complete();
							return;
						}
					}

					if (!unsubscribed) {
						observer.complete();
					}
				},
				{
					shouldRetryFn: (error) => this.shouldRetryFetch(error, [request]),
					onRetryFn: (error, attempt) => this.onRetryFn(error, [request], attempt),
					maxRetries: NETWORK_ERROR_MAX_RETRIES,
					initialDelayMs: NETWORK_ERROR_INITIAL_RETRY_DELAY_MS,
					maxDelayMs: NETWORK_ERROR_MAX_RETRY_DELAY_MS,
				},
			).catch((error) => {
				if (!unsubscribed) {
					observer.error(error);
				}
			});

			return () => {
				unsubscribed = true;
				abortController.abort();
			};
		});
	}

	/**
	 * Processes a batch of requests.
	 *
	 * For a single request, delegates to processSingleRequest directly.
	 * For multiple requests, builds a batch body and sends them as a single HTTP request.
	 * The batch endpoint URL (/slow/batch or /pq/batch) is chosen based on
	 * shouldUsePQEndpoint() at dispatch time.
	 *
	 * @param requests - An array of BatchedRequest objects.
	 * @returns A promise that resolves when the requests are processed.
	 */
	private async processBatch(requests: BatchedRequest[]): Promise<void> {
		if (requests.length === 0) {
			return;
		}

		if (requests.length === 1) {
			const [request] = requests;
			logPreloadBatchDebug('eligible-flushed-single', {
				operationName: request.request.name,
				requestId: request.id,
				batchInterval: this.batchingConfig.batchInterval,
				maxBatchSize: this.batchingConfig.maxBatchSize,
			});
			try {
				const result = await this.processSingleRequest(
					request.request,
					request.variables,
					request.cacheConfig,
				);
				request.resolve(result);
			} catch (error) {
				request.reject(error as Error);
			}
			return;
		}

		// Multiple requests — choose batch endpoint and body format based on experiment
		const operationNames = requests.map((req) => req.request.name);
		const usePQ = shouldUsePQEndpoint();
		const batchSafeMetadataHeaders = getCommonBatchSafeMetadataHeaders(requests);

		const batchEndpoint = usePQ ? getPQBatchEndpoint() : getBatchEndpoint(operationNames);
		logPreloadBatchDebug('batch-dispatch', {
			batchSize: requests.length,
			operationNames,
			usePQ,
			batchEndpoint,
			requestIds: requests.map((req) => req.id),
			batchSafeMetadataHeaderNames: Object.keys(batchSafeMetadataHeaders),
		});

		const batchBody = usePQ
			? getPQBatchRequestBody(
					requests.map((req) => ({
						id: req.request.id ?? null,
						operationName: req.request.name,
						variables: req.variables,
					})),
				)
			: JSON.stringify(
					requests.map((req: BatchedRequest) => ({
						operationName: req.request.name,
						variables: req.variables,
						...(req.request.text ? { query: req.request.text } : {}),
					})),
				);

		return retryFetch(
			async () => {
				const response = await wrappedFetch(batchEndpoint, {
					method: 'POST',
					credentials: 'same-origin',
					headers: {
						...batchSafeMetadataHeaders,
						'content-type': 'application/json',
						'X-ExperimentalApi': getRelayExperimentalApis().join(','),
					},
					body: batchBody,
				});

				if (!response.ok) {
					await logBatchResponseDebugInfo({
						response,
						batchEndpoint,
						operationNames,
						usePQ,
					});
				}

				let rawResponse: GraphQLSingularResponse | GraphQLSingularResponse[];

				try {
					rawResponse = await response.json();
				} catch (error) {
					// eslint-disable-next-line no-console
					console.log(
						`[DEBUG] Relay batch response JSON parsing failed ${stringifyDebugInfo({
							batchEndpoint,
							usePQ,
							operationNames,
							error: getErrorMessage(error),
						})}`,
					);
					throw error;
				}

				// Batch-level failure: server returns a single {errors} object (not an array)
				if (!Array.isArray(rawResponse)) {
					if (rawResponse != null) {
						recordRelaySSRHydrationResult(rawResponse);
					}
					// eslint-disable-next-line no-console
					console.log(
						`[DEBUG] Relay batch response was not an array ${stringifyDebugInfo({
							batchEndpoint,
							usePQ,
							operationNames,
							responseType: rawResponse === null ? 'null' : typeof rawResponse,
							responseErrorMessage: (rawResponse as any)?.errors?.[0]?.message,
						})}`,
					);
					const batchError = new Error(
						(rawResponse as any)?.errors?.[0]?.message ?? 'Batch request failed',
					);

					captureErrors((rawResponse as any)?.errors ?? [], {
						operationName: operationNames.join(','),
					});

					requests.forEach((req: BatchedRequest) => req.reject(batchError));
					throw batchError;
				}

				const batchResponse: GraphQLSingularResponse[] = rawResponse;

				requests.forEach((req: BatchedRequest, index: number) => {
					let batchedRes = batchResponse[index];

					// Guard against response array length mismatch
					if (batchedRes === undefined) {
						req.reject(new Error(`Missing batch response for ${req.request.name}`));
						return;
					}

					recordRelaySSRHydrationResult(batchedRes);

					if ('errors' in batchedRes && batchedRes.errors && batchedRes.errors.length > 0) {
						const errors = batchedRes.errors as AGGError[];
						const requestId = batchedRes?.extensions?.gateway?.request_id;

						captureErrors(errors, {
							operationName: req.request.name,
							requestId,
						});
					}

					// Apply query overrides if enabled
					if (shouldEnableQueryOverrides()) {
						batchedRes = applyRelayQueryOverrides(batchedRes, req.request);
					}

					req.resolve(batchedRes); // always resolve, reject only in case of network level error
				});
			},
			{
				onErrorFn: (error) => {
					// If batch request fails at network level, reject all requests
					requests.forEach((req: BatchedRequest) => {
						req.reject(error);
					});
				},
				shouldRetryFn: (error) =>
					this.shouldRetryFetch(
						error,
						requests.map((req) => req.request),
					),
				onRetryFn: (error, attempt) =>
					this.onRetryFn(
						error,
						requests.map((req) => req.request),
						attempt,
					),
				maxRetries: NETWORK_ERROR_MAX_RETRIES,
				initialDelayMs: NETWORK_ERROR_INITIAL_RETRY_DELAY_MS,
				maxDelayMs: NETWORK_ERROR_MAX_RETRY_DELAY_MS,
			},
		);
	}

	/**
	 * Schedules a batch of requests.
	 *
	 * @returns A promise that resolves when the requests are processed.
	 */
	private scheduleBatch(): void {
		if (this.batchTimeout) {
			clearTimeout(this.batchTimeout);
		}

		this.batchTimeout = setTimeout(() => {
			if (this.requestQueue.length > 0) {
				const batch = this.requestQueue.splice(0, this.batchingConfig.maxBatchSize);
				void this.processBatch(batch);

				// If there are more requests in the queue, schedule another batch
				if (this.requestQueue.length > 0) {
					this.scheduleBatch();
				}
			}
		}, this.batchingConfig.batchInterval);
	}

	/**
	 * Main method to fetch a request.
	 *
	 * @param request - The request to fetch.
	 * @param variables - The variables to fetch.
	 * @param cacheConfig - The cache config to fetch.
	 * @returns A promise that resolves when the request is processed.
	 */
	fetch(request: RequestParameters, variables: Variables, cacheConfig: CacheConfig): Promise<any> {
		// When the PQ endpoint experiment is OFF, the batch endpoint is `/slow/batch`
		// which requires `query` text in every item of the batch body. Operations whose
		// Relay artifact has no `text` (e.g. platform-hosted Forge queries and other
		// allowlisted operations — see `noQueryTextOperationNames`)
		// these operations they take the per-request PQ path instead.
		//
		// When PQ is ON the batch endpoint is `/pq/batch`, whose body carries an
		// `id`+`variables` pair per item and does not need `text` — text-less
		// operations can safely participate, so we do NOT bypass in that case.
		const usePQEndpoint = shouldUsePQEndpoint();
		const queryName = getQueryName(request.name, variables, true);
		const isFastQuery = isFastPreloadQuery(queryName);
		const metadataHeaders = getMetadataHeaders(cacheConfig);
		const metadataHeaderNames = Object.keys(metadataHeaders);
		const hasMetadataHeaders = metadataHeaderNames.length > 0;
		const unsafeMetadataHeaderNames = metadataHeaderNames.filter(
			(headerName) => !isBatchSafeMetadataHeader(headerName),
		);
		const hasUnsafeMetadataHeaders = unsafeMetadataHeaderNames.length > 0;
		const isIncremental = isIncrementalRequest(cacheConfig);
		// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
		const isSSR = Boolean(process.env.REACT_SSR);
		const bypassForMissingQueryText = !usePQEndpoint && isNoQueryTextOperation(request);

		const bypassForClientSide = isSSR && !isFastQuery;

		// Client-side query batching is disabled except for fast-preload queries on a page that was
		// not server-side rendered (an SPA transition or a non-SSR initial load).
		const clientSideBatchingDisabled = Boolean(window.__SSR_RENDERED__) || !isFastQuery;

		// This bypass logic mirrors Apollo's split logic
		const shouldBypassBatch =
			!this.batchingConfig.enabled ||
			Boolean(cacheConfig.metadata?.single) ||
			// Per-request headers (e.g. X-Query-Context) can only be honoured on the
			// single-request path. Batch-safe common headers are forwarded on the batch.
			hasUnsafeMetadataHeaders ||
			isIncremental ||
			bypassForMissingQueryText ||
			bypassForClientSide ||
			(!isSSR &&
				(cacheConfig.metadata?.throttle === true ||
					shouldDisableBatchingForEnvironment() ||
					clientSideBatchingDisabled));

		const shouldThrottle =
			!isSSR && !shouldDisableBatchingForEnvironment() && cacheConfig.metadata?.throttle === true;

		const bypassReasons = [
			!this.batchingConfig.enabled ? 'batching-disabled' : null,
			cacheConfig.metadata?.single === true ? 'metadata-single' : null,
			hasUnsafeMetadataHeaders ? 'metadata-headers' : null,
			isIncremental ? 'incremental' : null,
			bypassForMissingQueryText ? 'missing-query-text' : null,
			bypassForClientSide ? 'not-fast-in-ssr-region' : null,
			!isSSR && cacheConfig.metadata?.throttle === true ? 'client-throttle' : null,
			!isSSR && shouldDisableBatchingForEnvironment() ? 'client-environment-disabled' : null,
			!isSSR && clientSideBatchingDisabled ? 'client-fast-preload-disabled' : null,
		].filter((reason): reason is string => reason !== null);

		logPreloadBatchDebug('decision', {
			action: shouldBypassBatch ? 'single' : 'queue',
			operationName: request.name,
			queryName,
			isSSR,
			isFastQuery,
			batchingEnabled: this.batchingConfig.enabled,
			usePQEndpoint,
			bypassForMissingQueryText,
			metadataSingle: Boolean(cacheConfig.metadata?.single),
			hasMetadataHeaders,
			metadataHeaderNames,
			hasUnsafeMetadataHeaders,
			unsafeMetadataHeaderNames,
			isIncremental,
			shouldBypassBatch,
			bypassReasons,
			configGeneratedAt: preloadQueryBatchConfigGeneratedAt,
			configSourceUrl: preloadQueryBatchConfigSourceUrl,
		});

		if (shouldBypassBatch) {
			logPreloadBatchDebug('single-dispatch', {
				operationName: request.name,
				queryName,
				bypassReasons,
				metadataHeaderNames,
				unsafeMetadataHeaderNames,
			});
			return this.processSingleRequest(
				request,
				variables,
				cacheConfig,
				shouldThrottle,
				shouldBypassBatch,
			);
		}

		return new Promise((resolve, reject) => {
			const batchedRequest: BatchedRequest = {
				request,
				variables,
				cacheConfig,
				resolve,
				reject,
				id: this.generateRequestId(),
			};

			this.requestQueue.push(batchedRequest);

			if (this.requestQueue.length >= this.batchingConfig.maxBatchSize) {
				const batch = this.requestQueue.splice(0, this.batchingConfig.maxBatchSize);
				void this.processBatch(batch);
			} else {
				this.scheduleBatch();
			}
		});
	}

	public setBatchingConfig(config: Partial<BatchingConfig>): void {
		this.batchingConfig = { ...this.batchingConfig, ...config };
	}

	public getBatchingConfig(): BatchingConfig {
		return { ...this.batchingConfig };
	}

	public clearRequestQueue(): void {
		// Reject all pending requests
		this.requestQueue.forEach((req) => {
			req.reject(new Error('Request queue cleared'));
		});
		this.requestQueue = [];

		if (this.batchTimeout) {
			clearTimeout(this.batchTimeout);
			this.batchTimeout = null;
		}
	}
}
